// Single source of truth for World Farmers homepage content.
// All copy is sourced/refined from worldfarmers.org — no lorem ipsum.
// All images are locally downloaded from worldfarmers.org (no stock).

export const PHOTO_CREDIT = {
  label: "Photos by Emily Joy Ashman",
  href: "https://www.emilyjoyashman.com",
};

export const ORG = {
  name: "World Farmers",
  tagline: "Supporting farmers who feed our communities",
  missionShort:
    "World Farmers supports small farmers in sustainable agricultural production and successful marketing \u2014 connecting culturally relevant produce to viable markets.",
  missionFull:
    "World Farmers\u2019 mission is to support small farmers in sustainable agricultural production and successful marketing practices to connect culturally relevant produce to viable markets. We provide mentoring, training, and hands-on assistance when working with each farmer to build the capacity needed to operate individual farming enterprises. We enact our mission through various initiatives, the most prominent of which is the Flats Mentor Farm program.",
  phone: "(978) 706-7935",
  phoneHref: "tel:+19787067935",
  addressLines: ["769 Main St. Box 112", "Lancaster, MA 01523"],
  founded: 1984,
};

export const STATS = [
  { id: "farmers", value: "1,200", label: "Farmers supported" },
  { id: "acres", value: "55", label: "Acres in production" },
  { id: "markets", value: "40+", label: "Farmers\u2019 markets" },
  { id: "women", value: "60%", label: "Women farmers" },
];

export const NAV_INTENTS = [
  { id: "where-to-buy", label: "Where to Buy Crops", testid: "nav-where-to-buy-link" },
  { id: "support", label: "I Want to Support", testid: "nav-support-link" },
  { id: "who-we-are", label: "Who We Are", testid: "nav-who-we-are-link" },
];

export const INTENTS = [
  {
    id: "where-to-buy",
    kicker: "Shop the harvest",
    title: "Where to buy crops",
    body:
      "Find culturally relevant, farm-fresh produce at 40+ farmers\u2019 markets and direct-to-consumer outlets across Massachusetts and New England.",
    cta: "Find markets",
    image: "/images/wf/farm-035.jpg",
    alt: "A smiling farmer holding a freshly harvested bundle of leafy greens in a sunlit field.",
    feature: true,
  },
  {
    id: "support",
    kicker: "Fuel the mission",
    title: "I want to support",
    body:
      "Your gift helps immigrant and refugee farmers access land, training, and markets.",
    cta: "Donate today",
    image: "/images/wf/farmer6.jpg",
    alt: "A farmer smiling broadly while standing in a sunlit field of crops.",
    feature: false,
  },
  {
    id: "who-we-are",
    kicker: "Our story",
    title: "Who we are",
    body:
      "A community rooted in cross-cultural co-learning, growing since 1984 at the Flats Mentor Farm in Lancaster, MA.",
    cta: "Meet our community",
    image: "/images/wf/vlai/vlai-3794.jpg",
    alt: "A farmer in a pink apron walks through rows of crops with a freshly picked gourd.",
    feature: false,
  },
];

export const TIMELINE = [
  { year: "1984", text: "Immigrant and refugee farmers begin growing at the Flats in Lancaster, MA." },
  { year: "2005", text: "Farmers formally organize as the Flats Mentor Farm." },
  { year: "Today", text: "1,200+ farmers supported across 55 acres and 40+ markets." },
];

export const EVENT = {
  badge: "Featured event",
  title: "Tour de Farm 2026",
  date: "July 31, 2026",
  dateParts: { month: "JUL", day: "31", year: "2026" },
  location: "Flats Mentor Farm \u00b7 Lancaster, MA",
  body:
    "Save the date for a day among the fields \u2014 celebrate the farmers, taste the harvest, and see sustainable agriculture in action at our signature community gathering.",
  cta: "View event details",
  href: "/events/tour-de-farm-2026",
  image: "/images/wf/farm-2611.jpg",
  alt: "A silhouette of a farmer working in a field during a vibrant, sun-drenched sunrise.",
  ticketingNote: "Online ticketing coming soon.",
};

export const PROGRAMS = {
  flats: {
    badge: "Since 1985",
    title: "Flats Mentor Farm",
    body:
      "On a 70-acre river-bottom parcel in Lancaster, MA, the Flats gives small farmers of diverse backgrounds the land, infrastructure, and marketing support to build successful, sustainable farming enterprises \u2014 with hands-on training in soil fertility, irrigation, pest management, and markets.",
    image: "/images/wf/vlai/vlai-3552.jpg",
    alt: "Hoop houses stand beneath a bright blue sky dotted with clouds at the Flats Mentor Farm.",
  },
  mentorship: {
    badge: "Mentorship",
    title: "A five-phase path to independence",
    body:
      "Our structured farmer-development model meets growers where they are \u2014 from first season to fully independent business \u2014 through peer learning, targeted training, and market access.",
    phases: [
      { n: 1, title: "First-year & subsistence", text: "Farmers enter the community and grow for their families as they learn the land and climate." },
      { n: 2, title: "Increased land & production", text: "Growers scale up through farmer exchanges, production planning, and targeted trainings." },
      { n: 3, title: "Market-ready", text: "Farmers begin selling \u2014 supported in crop planning, food safety, and market introductions." },
      { n: 4, title: "Commercial", text: "Independent farm businesses with the financial literacy to secure loans and equipment." },
      { n: 5, title: "Independent", text: "Farmers manage their own land, sustained by long-term partnership and networks." },
    ],
  },
};

export const NEWS = [
  {
    id: "tour-de-farm",
    category: "Event",
    title: "Save the Date! Tour de Farm 2026",
    date: "Announced June 2026",
    excerpt:
      "Our signature community celebration returns to the Flats \u2014 mark your calendar and join us in the fields this summer.",
    href: "https://www.worldfarmers.org/tour-de-farm/",
    image: "/images/wf/vlai/vlai-3475.jpg",
    alt: "Young seedlings pushing up through dark, freshly turned soil.",
    featured: true,
  },
  {
    id: "supporting-farmers",
    category: "Mission",
    title: "Supporting farmers who feed our communities",
    date: "December 2025",
    excerpt:
      "A look at how culturally relevant produce grown at the Flats reaches tables across New England.",
    href: "https://www.worldfarmers.org/supporting-farmers-who-feed-our-communities/",
    image: "/images/wf/vlai/vlai-3509.jpg",
    alt: "A farmer holds up a freshly picked bunch of leafy greens.",
    featured: false,
  },
  {
    id: "new-ed",
    category: "Announcement",
    title: "Great news \u2014 welcoming new leadership to World Farmers",
    date: "July 2025",
    excerpt:
      "A new chapter of leadership for World Farmers as we grow our impact for the next generation of farmers.",
    href: "https://www.worldfarmers.org/new-executive-director/",
    image: "/images/wf/farmer5.jpg",
    alt: "A smiling woman standing in a field holding a bundle of harvested produce.",
    featured: false,
  },
];

export const GALLERY = [
  { src: "/images/wf/farm-fabiola.jpg", alt: "A woman in colorful traditional clothing harvesting greens in an open field under a dramatic sky.", span: true },
  { src: "/images/wf/vlai/vlai-3472.jpg", alt: "A farmer seen through the open doorway of a wooden hoop house at the Flats Mentor Farm." },
  { src: "/images/wf/vlai/vlai-3497.jpg", alt: "A farmer in a white head wrap harvests greens inside a sunlit high tunnel." },
  { src: "/images/wf/farm-017.jpg", alt: "A farmer laughing in a baseball cap while walking through a sunlit field of tall crops." },
  { src: "/images/wf/vlai/vlai-3579.jpg", alt: "A farmer bends low to harvest from a row of leafy plants." },
  { src: "/images/wf/farm-36.jpg", alt: "A smiling farmer in a plaid shirt and sun hat standing in a bright field of greens.", span: true },
  { src: "/images/wf/vlai/vlai-3626.jpg", alt: "A farmer kneels beside a basket filling with freshly picked peppers." },
  { src: "/images/wf/vlai/vlai-3767.jpg", alt: "A farmer stands among tall trellised vines under a blue sky." },
  { src: "/images/wf/vlai/vlai-3799.jpg", alt: "A farmer in a pink apron pauses in the field holding a freshly picked gourd." },
  { src: "/images/wf/vlai/vlai-3851.jpg", alt: "A farmer kneels among leafy plants to harvest." },
  { src: "/images/wf/vlai/vlai-3871.jpg", alt: "A farmer pushes a wheelbarrow of harvested greens down a farm road lined with hoop houses." },
  { src: "/images/wf/farm-159.jpg", alt: "A farmer wearing a wide-brimmed hat bends over rows of crops at golden hour." },
];
